import { BinIcon, PlusIcon } from '@systemeio/ui/icons'
import { DatePicker, Hint, NeutralButton, NeutralTypeEnum, TimePicker } from '@systemeio/ui'
import { greenwichTimeZone } from 'modules/calendar/constants'
import {
  getDateOnly,
  shortDateToGreenwichLongDate,
} from 'modules/calendar/utils/date-time-formatters'
import React from 'react'
import ButtonLikeLink from 'shared/components/button-like-link'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { twMerge } from 'tailwind-merge'
import { AvailabilitySlot, AvailabilitySlotId } from '../types/availability-interface'
import { AvailabilitySlotsSkeleton } from './availability-slots-skeleton'

interface DateSpecificAvailabilitySlotsProps {
  slots: AvailabilitySlot[]
  addSlot: (slot: Omit<AvailabilitySlot, 'id'>) => void
  updateSlot: (slot: AvailabilitySlot) => void
  removeSlots: (slotIds: AvailabilitySlotId[]) => void
  isLoading?: boolean
  disabled?: boolean
  minDate?: Date
  maxDate?: Date
  titleClassName?: string
}

export function DateSpecificAvailabilitySlots({
  slots,
  addSlot,
  updateSlot,
  removeSlots,
  isLoading,
  disabled,
  minDate,
  maxDate,
  titleClassName,
}: DateSpecificAvailabilitySlotsProps) {
  const { t } = useLocoTranslation()
  const { user, intlLocale } = useUser()

  return (
    <>
      <div
        className={twMerge(
          'flex w-full flex-col items-start gap-2 md:flex-row md:items-center md:justify-between',
          slots.length > 0 && 'mb-5',
          titleClassName,
        )}
      >
        <Hint label={t('dashboard.calendar.date_specific_availability_tooltip')}>
          <p>{t('dashboard.calendar.date_specific_availability')}</p>
        </Hint>

        <ButtonLikeLink
          type="button"
          onClick={() => {
            addSlot({
              date: '',
              startTime: null,
              endTime: null,
              dayOfWeek: null,
            })
          }}
          className={'flex items-center gap-1 text-sm'}
          disabled={disabled}
        >
          <PlusIcon />
          {t('dashboard.calendar.add_date_slot')}
        </ButtonLikeLink>
      </div>
      <div>
        {isLoading && <AvailabilitySlotsSkeleton />}
        {!isLoading &&
          slots?.map(slot => (
            <div
              key={slot.id}
              className="border-text-secondary/30 flex flex-col gap-2 border-t py-5 last:border-b md:justify-between 2xl:flex-row 2xl:gap-10"
            >
              <div>
                <DatePicker
                  value={slot.date && shortDateToGreenwichLongDate(slot.date)}
                  onChange={dateTime => {
                    if (dateTime) {
                      updateSlot({ ...slot, date: getDateOnly(dateTime) })
                    }
                  }}
                  minDate={minDate}
                  maxDate={maxDate}
                  placeholder={t('global.select_date')}
                  disabled={disabled}
                  timezone={greenwichTimeZone}
                  createdAt={user?.createdAt}
                  intlLocale={intlLocale}
                />
              </div>
              <div className="flex flex-1 md:justify-between">
                <div className="[&>input]:w-full flex flex-1 gap-1 md:gap-5">
                  <TimePicker
                    seconds={slot.startTime}
                    onChangeSeconds={seconds => {
                      updateSlot({ ...slot, startTime: seconds })
                    }}
                    disabled={disabled}
                    userDateFormat={user?.dateFormat}
                    userTimeFormat={user?.timeFormat}
                  />
                  <TimePicker
                    seconds={slot.endTime}
                    onChangeSeconds={seconds => {
                      updateSlot({ ...slot, endTime: seconds })
                    }}
                    disabled={disabled}
                    userDateFormat={user?.dateFormat}
                    userTimeFormat={user?.timeFormat}
                  />
                </div>
                <NeutralButton
                  type="button"
                  neutralType={NeutralTypeEnum.danger}
                  onClick={() => removeSlots([slot.id])}
                  disabled={disabled}
                >
                  <BinIcon
                    width={16}
                    height={16}
                    className="fill-text-primary group-hover:fill-danger transition-all"
                  />
                  <span className="hidden md:block">{t('global.remove')}</span>
                </NeutralButton>
              </div>
            </div>
          ))}
      </div>
    </>
  )
}
